import React, { useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { isMobileBelowWidth } from '@utilities/consts';
import Header from '@features/PageBuilder/components/Header';
import Container from '@components/Container/Container';
import { useEmblaCarousel } from 'embla-carousel/react';
import ConditionalWrapper from '@components/ConditionalWrapper';
import SliderPagination from '@components/SliderPagination';
import useWindowSize from '@hooks/use-window-size';
import styles from './PriceList.module.css';
import { HeaderSettings, Languages } from '@shared/types';
import { Subscriptions } from '@shared/types/cancellation';
import { useRouter } from 'next/router';
import PriceModel from './components/PriceModel/PriceModel';
import ProductPricing from './components/ProductPricing/ProductPricing';

const {
  Root,
  PriceModelsWrapper,
  ContainerGap,
  WhiteBackground,
  PriceModelBox,
  SliderContainer,
  SliderPagination: SliderPaginationClass,
  TopSpacing,
} = styles;

interface PriceListProps {
  className?: string;
  header: string;
  subheader?: string;
  prices: object;
  group: 'recurring' | 'singlewash' | 'self-wash';
  backgroundColor: string;
  business: boolean;
  campaignPrice: number;
  isCampaign: boolean;
  headerSettings: HeaderSettings;
}

const PriceList = ({
  className,
  header,
  subheader = '',
  prices = {},
  group = 'recurring',
  backgroundColor,
  business: isBusiness,
  campaignPrice = 0,
  isCampaign,
  headerSettings = {},
}: PriceListProps) => {
  /*
   *    HOOKS & VARIABLES
   */

  const router = useRouter();
  const windowSize = useWindowSize();
  const isMobileReady = typeof windowSize.width === 'number';

  const isMobile =
    isMobileReady && windowSize.width && windowSize.width < isMobileBelowWidth;

  const classes = classnames(
    Root,
    {
      [WhiteBackground]: backgroundColor === 'white',
    },
    className
  );

  const priceModels =
    prices?.models.sort(
      ({ price: priceA }, { price: priceB }) => priceA - priceB
    ) ?? [];

  const indexOfPopular = priceModels.findIndex(({ isPopular }) => isPopular);
  const isDkWebsite = router.locale === Languages.DA.toLocaleLowerCase();
  const showNewProductPricing = isDkWebsite;

  const [carouselIsActive, setCarouselIsActive] = useState(isMobile);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [emblaRef, embla] = useEmblaCarousel({
    slidesToScroll: 1,
    align: 'start',
    startIndex: indexOfPopular,
  });

  /*
   *    HANDLERS
   */

  const scrollTo = useCallback(
    (index: number) => embla?.scrollTo(index),
    [embla]
  );

  const onInit = useCallback(() => {
    if (!embla) return;
    setCarouselIsActive(embla.slidesNotInView().length > 0);
  }, [embla]);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
  }, [embla, setSelectedIndex]);

  /*
   *    USEEFFECTS
   */

  useEffect(() => {
    if (!isMobileReady) return;
    setCarouselIsActive(isMobile);
  }, [isMobileReady, isMobile]);

  useEffect(() => {
    if (!embla) return;
    if (!isMobile) return;
    if (showNewProductPricing) return;
    onSelect();
    embla.on('select', onSelect);
  }, [embla, onSelect, showNewProductPricing, isMobile]);

  useEffect(() => {
    if (!embla) return;
    if (!isMobile) return;
    if (showNewProductPricing) return;
    embla.on('resize', embla.reInit).on('reInit', onInit);
    onInit();
  }, [embla, onInit, showNewProductPricing, isMobile]);

  if (isMobile && showNewProductPricing) {
    return (
      <Container className={classes}>
        <Header
          text={header}
          subheader={subheader}
          position="center"
          headerSettings={headerSettings}
        />

        <div className={TopSpacing}>
          {priceModels.map((product: Subscriptions, index: number) => (
            <ProductPricing
              key={`${product.id}-${index}`}
              id={product.id}
              name={product.name}
              isBusiness={isBusiness}
              singleWashPrice={product.single_wash_price}
              price={product.price}
              isSingleWash={group === 'singlewash'}
              isPopular={product.is_popular}
              description={product.short_description}
              features={product.features}
              productLink={product.link.url}
              isCampaign={isCampaign}
              campaignPrice={campaignPrice}
              isMobile
            />
          ))}
        </div>
      </Container>
    );
  }

  return (
    <Container className={classes}>
      <Header
        text={header}
        subheader={subheader}
        position="center"
        headerSettings={headerSettings}
      />

      {carouselIsActive && isMobile && (
        <SliderPagination
          className={SliderPaginationClass}
          total={prices?.models.length}
          current={selectedIndex}
          scrollTo={scrollTo}
        />
      )}

      <div
        ref={carouselIsActive && isMobile ? emblaRef : null}
        className={`${PriceModelsWrapper} ${
          showNewProductPricing ? ContainerGap : ''
        }`}
      >
        <ConditionalWrapper
          condition={carouselIsActive}
          wrapper={(children) => (
            <>
              <div className={SliderContainer}>{children}</div>
            </>
          )}
        >
          {priceModels.map((product: Subscriptions, index: number) => {
            if (showNewProductPricing) {
              return (
                <ProductPricing
                  key={`${product.id}-${index}`}
                  id={product.id}
                  name={product.name}
                  isBusiness={isBusiness}
                  singleWashPrice={product.single_wash_price}
                  price={product.price}
                  isSingleWash={group === 'singlewash'}
                  isPopular={product.is_popular}
                  description={product.short_description}
                  features={product.features}
                  productLink={product.link.url}
                  isCampaign={isCampaign}
                  campaignPrice={campaignPrice}
                />
              );
            } else {
              return (
                <PriceModel
                  className={PriceModelBox}
                  {...product}
                  signupFee={prices.signup_fee}
                  key={product.id}
                  isBusiness={isBusiness}
                  group={group}
                  whiteBackground={backgroundColor === 'white'}
                  isCampaign={isCampaign}
                  campaignPrice={campaignPrice}
                />
              );
            }
          })}
        </ConditionalWrapper>
      </div>
    </Container>
  );
};

export default PriceList;
